import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

const normalizeId = (id) => String(id ?? "");

const getDefaultExpandedIds = (nodes, maxDepth) => {
  const expanded = new Set();
  if (maxDepth <= 0) return expanded;

  const traverse = (list, depth) => {
    for (const node of list) {
      const id = normalizeId(node.id);
      const children = Array.isArray(node.children) ? node.children : [];
      if (children.length > 0 && depth < maxDepth) {
        expanded.add(id);
      }
      if (children.length > 0) {
        traverse(children, depth + 1);
      }
    }
  };

  traverse(nodes, 0);
  return expanded;
};

const TreeNode = memo(function TreeNode({
  node,
  depth,
  expandedIds,
  selectedNodeId,
  onToggle,
  onSelectNode,
}) {
  const id = normalizeId(node.id);
  const children = Array.isArray(node.children) ? node.children : [];
  const hasChildren = children.length > 0;
  const isExpanded = expandedIds.has(id);
  const isSelected = selectedNodeId === id;
  const levelStyles = [
    "text-blue-700",
    "text-emerald-700",
    "text-amber-700",
    "text-violet-700",
    "text-rose-700",
    "text-cyan-700",
  ];
  const levelClass = levelStyles[depth % levelStyles.length];

  return (
    <div>
      <div
        className={`flex items-center gap-2 rounded-md px-2 py-1 text-sm ${
          isSelected ? "bg-slate-100 text-slate-900" : `hover:bg-gray-50 ${levelClass}`
        }`}
        style={{ paddingLeft: `${depth * 14 + 6}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggle(node.id)}
            className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-gray-800"
            aria-label={isExpanded ? "Collapse node" : "Expand node"}
          >
            {isExpanded ? "-" : "+"}
          </button>
        ) : (
          <span className="w-4 h-4 inline-block" />
        )}

        <button
          type="button"
          onClick={() => onSelectNode?.(node)}
          className="flex-1 text-left truncate"
          title={node.name}
        >
          {node.name}
        </button>
      </div>

      {hasChildren && isExpanded && (
        <div>
          {children.map((child) => (
            <TreeNode
              key={normalizeId(child.id)}
              node={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              selectedNodeId={selectedNodeId}
              onToggle={onToggle}
              onSelectNode={onSelectNode}
            />
          ))}
        </div>
      )}
    </div>
  );
});

const AoiTree = memo(function AoiTree({
  nodes,
  selectedNodeId = null,
  onSelectNode,
  defaultExpandedDepth = 1,
  className = "",
  emptyLabel = "Aucune zone disponible",
}) {
  const safeNodes = useMemo(() => (Array.isArray(nodes) ? nodes : []), [nodes]);
  const selectedId = useMemo(() => normalizeId(selectedNodeId), [selectedNodeId]);

  const [expandedIds, setExpandedIds] = useState(() =>
    getDefaultExpandedIds(safeNodes, defaultExpandedDepth),
  );

  useEffect(() => {
    setExpandedIds(getDefaultExpandedIds(safeNodes, defaultExpandedDepth));
  }, [safeNodes, defaultExpandedDepth]);

  const toggleNode = useCallback((id) => {
    const normalized = normalizeId(id);
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(normalized)) next.delete(normalized);
      else next.add(normalized);
      return next;
    });
  }, []);

  if (safeNodes.length === 0) {
    return <div className={`text-xs text-gray-500 ${className}`}>{emptyLabel}</div>;
  }

  return (
    <div className={className}>
      {safeNodes.map((node) => (
        <TreeNode
          key={normalizeId(node.id)}
          node={node}
          depth={0}
          expandedIds={expandedIds}
          selectedNodeId={selectedId}
          onToggle={toggleNode}
          onSelectNode={onSelectNode}
        />
      ))}
    </div>
  );
});

export default AoiTree;
